import { DeprecatedCloseIcon, DeprecatedDoneIcon, EditIcon, IconButton, Input, LoadingSignIcon } from '@lokalise/louis';
import { type FocusEvent, type KeyboardEvent, type ReactNode, useState } from 'react';

import { InlineValueEditorContainer, InlineValueEditorIconButton } from './styles';

type InlineValueEditorBaseProps = {
    value: string;
    placeholder?: string;
    responseHandler?: (stagedValue: string) => void;

    areaLabel: string;
    submitAreaLabel?: string;
    cancelAreaLabel?: string;
    editAreaLabel?: string;

    required?: boolean;
    readonly?: boolean;
    submitOnBlur?: boolean;
    loading?: boolean;

    displayValue?: string | ReactNode;

    onEditStart?: () => void;
    onEditCanceled?: () => void;
    onEditSubmitted?: (newValue: string, oldValue: string) => void;

    error?: string | boolean | string[];
    open: boolean;
};

const InlineValueEditor = ({
    value,
    placeholder,
    areaLabel,
    cancelAreaLabel,
    editAreaLabel,
    submitAreaLabel,
    onEditCanceled,
    onEditSubmitted,
    onEditStart,
    readonly,
    required,
    loading,
    submitOnBlur,
    responseHandler,
    displayValue,

    error,
    open,
}: InlineValueEditorBaseProps) => {
    const [stagedValue, setStagedValue] = useState(value);

    const startEditing = () => {
        if (readonly) {
            return;
        }

        onEditStart?.();
    };

    const cancelEditing = (resetValue: boolean) => {
        if (resetValue) {
            setStagedValue(value);
        }

        onEditCanceled?.();
    };

    const submitEdit = async () => {
        await responseHandler?.(stagedValue);

        if (stagedValue === value) {
            return;
        }

        await onEditSubmitted?.(stagedValue, value);
    };

    const handleKeyUp = (evt: KeyboardEvent<HTMLInputElement>) => {
        if (evt.key === 'Enter') {
            submitEdit();
        }

        if (evt.key === 'Escape') {
            cancelEditing(true);
        }
    };

    const onBlur = (e: FocusEvent<HTMLDivElement, Element>) => {
        // Prevent clicking submit/cancel buttons triggering parent onBlur
        if (e.currentTarget.contains(e.relatedTarget)) {
            return;
        }

        if (submitOnBlur) {
            submitEdit();
        } else {
            cancelEditing(false);
        }
    };

    if (!open) {
        return (
            <InlineValueEditorContainer $editable={!readonly}>
                {displayValue || <span>{stagedValue}</span>}
                <IconButton ariaLabel={editAreaLabel ?? 'Edit value'} onClick={startEditing}>
                    <EditIcon />
                </IconButton>
            </InlineValueEditorContainer>
        );
    }

    return (
        <InlineValueEditorContainer $editing $error={!!error} onBlur={onBlur}>
            <Input
                autoFocus
                error={error}
                required={required}
                value={stagedValue}
                aria-label={areaLabel}
                placeholder={placeholder ?? 'Enter value'}
                onChange={(e) => setStagedValue(e.currentTarget.value)}
                onKeyUp={handleKeyUp}
                suffix={
                    <>
                        {loading ? (
                            <LoadingSignIcon ariaLabel="Loading" size="24px" />
                        ) : (
                            <InlineValueEditorIconButton
                                variant="primary"
                                ariaLabel={submitAreaLabel ?? 'Submit edit'}
                                onClick={submitEdit}
                                loading={loading}
                            >
                                <DeprecatedDoneIcon />
                            </InlineValueEditorIconButton>
                        )}

                        <InlineValueEditorIconButton
                            variant="secondary"
                            ariaLabel={cancelAreaLabel ?? 'Cancel edit'}
                            onClick={() => cancelEditing(true)}
                        >
                            <DeprecatedCloseIcon />
                        </InlineValueEditorIconButton>
                    </>
                }
            />
        </InlineValueEditorContainer>
    );
};

export default InlineValueEditor;
